import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export interface AuditTag {
  auditId: string;
  triggeredBy: string;
  displayName: string;
  url: string;
  createdAt: string;
}

/**
 * API for managing audit tags (who triggered an audit, display names, etc.).
 * Not to be confused with LighthouseApiWithTags, which wraps the core
 * Lighthouse audit API to automatically tag audits on trigger.
 */
export interface LighthouseTagsApi {
  getTags(filters?: {
    auditId?: string;
    triggeredBy?: string;
    url?: string;
  }): Promise<AuditTag[]>;

  setTag(auditId: string, url: string, displayName?: string): Promise<AuditTag>;

  deleteTag(auditId: string): Promise<void>;
}

export const lighthouseTagsApiRef = createApiRef<LighthouseTagsApi>({
  id: 'plugin.lighthouse-tags',
});

export class LighthouseTagsClient implements LighthouseTagsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('lighthouse-tags');
  }

  async getTags(filters?: {
    auditId?: string;
    triggeredBy?: string;
    url?: string;
  }): Promise<AuditTag[]> {
    const base = await this.baseUrl();
    const params = new URLSearchParams();
    if (filters?.auditId) params.set('auditId', filters.auditId);
    if (filters?.triggeredBy) params.set('triggeredBy', filters.triggeredBy);
    if (filters?.url) params.set('url', filters.url);

    const query = params.toString();
    const url = `${base}/tags${query ? `?${query}` : ''}`;
    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch tags: ${response.statusText}`);
    }

    return await response.json();
  }

  async setTag(auditId: string, url: string, displayName?: string): Promise<AuditTag> {
    const base = await this.baseUrl();
    const body: Record<string, string> = { auditId, url };
    if (displayName) {
      body.displayName = displayName;
    }

    const response = await this.fetchApi.fetch(`${base}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Failed to create tag: ${response.statusText}`);
    }

    return await response.json();
  }

  async deleteTag(auditId: string): Promise<void> {
    const base = await this.baseUrl();
    const response = await this.fetchApi.fetch(`${base}/tags/${encodeURIComponent(auditId)}`, {
      method: 'DELETE',
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete tag: ${response.statusText}`);
    }
  }
}